import type { PropsWithChildren } from "react";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type AuthUser = {
  id: string;
  name: string;
  email: string;
  subscriptionActive: boolean;
  trialExpiresAt: string | null;
};

type SignInPayload = {
  email: string;
  password: string;
  rememberMe: boolean;
};

type SignUpPayload = {
  name: string;
  email: string;
  password: string;
  rememberMe: boolean;
};

type PasswordResetConfirmPayload = {
  email: string;
  otp: string;
  password: string;
};

type TrialInfo = {
  expiresAt: string | null;
  isActive: boolean;
  subscriptionActive: boolean;
};

type SessionDevice = {
  name: string | null;
  userAgent: string | null;
  ipAddress: string | null;
};

export type ActiveSession = {
  id: string;
  tokenId: string;
  current: boolean;
  createdAt: string | null;
  lastActiveAt: string | null;
  expiresAt: string | null;
  rememberMe: boolean | null;
  device: SessionDevice;
};

type AuthContextValue = {
  currentUser: AuthUser | null;
  loading: boolean;
  subscriptionRequired: boolean;
  trialInfo: TrialInfo | null;
  signIn: (credentials: SignInPayload) => Promise<void>;
  signUp: (payload: SignUpPayload) => Promise<void>;
  signOut: () => Promise<void>;
  clearSubscriptionRequired: () => void;
  sessions: ActiveSession[];
  refreshSessions: () => Promise<void>;
  revokeSession: (tokenId: string) => Promise<void>;
  revokeOtherSessions: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (payload: PasswordResetConfirmPayload) => Promise<void>;
};

const API_BASE_URL = process.env.REACT_APP_API_URL;
const LOCAL_TOKEN_KEY = "salesforce.auth.token";
const SESSION_TOKEN_KEY = "salesforce.auth.sessionToken";
const AUTH_REFRESH_INTERVAL_MS = 2 * 60 * 1000;

const AuthContext = createContext<AuthContextValue | undefined>(undefined);
const isBrowser = typeof window !== "undefined";

const safeStorage = {
  getItem(storage: Storage | undefined, key: string) {
    try {
      return storage?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  setItem(storage: Storage | undefined, key: string, value: string) {
    try {
      storage?.setItem(key, value);
    } catch {
      // ignore storage errors (private browsing, etc.)
    }
  },
  removeItem(storage: Storage | undefined, key: string) {
    try {
      storage?.removeItem(key);
    } catch {
      // ignore storage errors
    }
  },
};

const setToken = (token: string, rememberMe: boolean) => {
  if (!isBrowser) {
    return;
  }

  if (rememberMe) {
    safeStorage.setItem(window.localStorage, LOCAL_TOKEN_KEY, token);
    safeStorage.removeItem(window.sessionStorage, SESSION_TOKEN_KEY);
  } else {
    console.log("setting session....");
    safeStorage.setItem(window.sessionStorage, SESSION_TOKEN_KEY, token);
    safeStorage.removeItem(window.localStorage, LOCAL_TOKEN_KEY);
  }
};

const loadToken = () => {
  if (!isBrowser) {
    return { token: null as string | null, rememberMe: false };
  }

  const sessionToken = safeStorage.getItem(
    window.sessionStorage,
    SESSION_TOKEN_KEY
  );
  if (sessionToken) {
    return { token: sessionToken, rememberMe: false };
  }

  const localToken = safeStorage.getItem(window.localStorage, LOCAL_TOKEN_KEY);
  if (localToken) {
    return { token: localToken, rememberMe: true };
  }

  return { token: null as string | null, rememberMe: false };
};

const clearStoredTokens = () => {
  if (!isBrowser) {
    return;
  }

  safeStorage.removeItem(window.localStorage, LOCAL_TOKEN_KEY);
  safeStorage.removeItem(window.sessionStorage, SESSION_TOKEN_KEY);
};

const normalizeDate = (value: string | Date | null | undefined) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

const mapUser = (user: any): AuthUser => ({
  id: user?.id ?? "",
  name: user?.name ?? "",
  email: user?.email ?? "",
  subscriptionActive: Boolean(user?.subscriptionActive),
  trialExpiresAt: normalizeDate(user?.trialExpiresAt),
});

const mapSessionDevice = (device: any): SessionDevice => {
  const name =
    typeof device?.name === "string" && device.name.trim().length > 0
      ? device.name.trim()
      : null;
  const userAgent =
    typeof device?.userAgent === "string" && device.userAgent.trim().length > 0
      ? device.userAgent.trim()
      : null;
  const ipAddress =
    typeof device?.ipAddress === "string" && device.ipAddress.trim().length > 0
      ? device.ipAddress.trim()
      : null;

  return {
    name,
    userAgent,
    ipAddress,
  };
};

const mapActiveSession = (session: any): ActiveSession | null => {
  if (
    !session ||
    (typeof session.tokenId !== "string" && typeof session.id !== "string")
  ) {
    return null;
  }

  const tokenId =
    typeof session.tokenId === "string" ? session.tokenId : session.id;

  const rememberMeValue = session?.rememberMe;
  const rememberMe =
    typeof rememberMeValue === "boolean"
      ? rememberMeValue
      : rememberMeValue === null
      ? null
      : null;

  return {
    id: tokenId,
    tokenId,
    current: Boolean(session?.current),
    createdAt: normalizeDate(session?.createdAt),
    lastActiveAt: normalizeDate(session?.lastActiveAt),
    expiresAt: normalizeDate(session?.expiresAt),
    rememberMe,
    device: mapSessionDevice(session?.device),
  };
};

export const AuthProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionRequired, setSubscriptionRequired] = useState(false);
  const [trialInfo, setTrialInfo] = useState<TrialInfo | null>(null);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const isRefreshingRef = useRef(false);

  const clearAuthState = useCallback(() => {
    clearStoredTokens();
    setCurrentUser(null);
    setTrialInfo(null);
    setSubscriptionRequired(false);
    setSessions([]);
  }, []);

  const applySessions = useCallback((rawSessions: any): ActiveSession[] => {
    const list = Array.isArray(rawSessions) ? rawSessions : [];
    const mapped = list
      .map(mapActiveSession)
      .filter(
        (session): session is ActiveSession =>
          Boolean(session) && typeof session?.tokenId === "string"
      );
    setSessions(mapped);
    return mapped;
  }, []);

  const fetchWithAuth = useCallback(
    async (path: string, options: RequestInit = {}, tokenOverride?: string) => {
      const { token } = loadToken();
      const authToken = tokenOverride ?? token;
      const headers = new Headers(options.headers || {});

      if (authToken) {
        headers.set("Authorization", `Bearer ${authToken}`);
      }
      if (!headers.has("Content-Type")) {
        headers.set("Content-Type", "application/json");
      }

      const response = await fetch(`${API_BASE_URL}${path}`, {
        ...options,
        headers,
      });

      if (response.status === 401) {
        clearAuthState();
      }

      return response;
    },
    [clearAuthState]
  );

  const refreshSessions = useCallback(async () => {
    try {
      const response = await fetchWithAuth("/auth/sessions", {
        method: "GET",
      });

      if (!response.ok) {
        if (response.status !== 401) {
          throw new Error("Failed to load active sessions.");
        }
        return;
      }

      const data = await response.json().catch(() => null);
      applySessions(data?.sessions);
    } catch (error) {
      console.warn("Failed to refresh sessions:", error);
    }
  }, [fetchWithAuth, applySessions]);

  const applyAuthState = useCallback(
    (payload: any, rememberMe: boolean) => {
      const token: string | undefined = payload?.token;
      const user = mapUser(payload?.user);
      const trialExpiresAt = normalizeDate(
        payload?.trialExpiresAt ?? payload?.user?.trialExpiresAt
      );
      const trialActive = Boolean(payload?.trialActive);
      const subscriptionActive = Boolean(payload?.subscriptionActive);

      if (!token || !user.id) {
        throw new Error("Authentication response missing required fields.");
      }

      setToken(token, rememberMe);
      setCurrentUser(user);
      setTrialInfo({
        expiresAt: trialExpiresAt,
        isActive: trialActive,
        subscriptionActive,
      });
      setSubscriptionRequired(false);

      const mapped = applySessions(payload?.sessions);
      if (!payload?.sessions && mapped.length === 0) {
        void refreshSessions();
      }
    },
    [applySessions, refreshSessions]
  );

  const handleSubscriptionRequired = useCallback(
    (payload: Partial<TrialInfo> & { message?: string }) => {
      clearStoredTokens();
      setCurrentUser(null);
      setSessions([]);
      setTrialInfo({
        expiresAt: payload.expiresAt ?? null,
        isActive: Boolean(payload.isActive),
        subscriptionActive: Boolean(payload.subscriptionActive),
      });
      setSubscriptionRequired(true);
    },
    []
  );

  const applyMeResponse = useCallback(
    (data: any) => {
      const trialExpiresAt = normalizeDate(
        data?.trialExpiresAt ?? data?.user?.trialExpiresAt
      );
      const trialActive = Boolean(data?.trialActive);
      const subscriptionActive = Boolean(data?.subscriptionActive);

      if (!trialActive && !subscriptionActive) {
        handleSubscriptionRequired({
          expiresAt: trialExpiresAt,
          isActive: false,
          subscriptionActive,
        });
        return;
      }

      setCurrentUser(mapUser(data.user));
      setTrialInfo({
        expiresAt: trialExpiresAt,
        isActive: trialActive,
        subscriptionActive,
      });
      setSubscriptionRequired(false);
      applySessions(data?.sessions);
    },
    [handleSubscriptionRequired, applySessions]
  );

  const refreshCurrentUser = useCallback(
    async (options?: { initial?: boolean }) => {
      const initial = options?.initial ?? false;

      // Don't set loading to false if already refreshing
      if (isRefreshingRef.current) {
        return;
      }

      const { token } = loadToken();

      // If no token found, set loading to false and return
      if (!token) {
        if (initial) {
          setLoading(false);
        }
        return;
      }

      isRefreshingRef.current = true;

      try {
        const response = await fetchWithAuth(
          "/auth/me",
          {
            method: "GET",
          },
          token
        );

        if (!response.ok) {
          if (response.status === 401) {
            clearAuthState();
            return;
          }
          throw new Error("Unable to refresh session.");
        }

        const data = await response.json();
        applyMeResponse(data);
      } catch (error) {
        console.warn("Failed to refresh auth session:", error);
        clearAuthState();
      } finally {
        isRefreshingRef.current = false;
        if (initial) {
          setLoading(false);
        }
      }
    },
    [fetchWithAuth, applyMeResponse, clearAuthState]
  );

  useEffect(() => {
    void refreshCurrentUser({ initial: true });
  }, [refreshCurrentUser]);

  useEffect(() => {
    if (!currentUser) {
      return;
    }

    const interval = window.setInterval(() => {
      void refreshCurrentUser();
    }, AUTH_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [currentUser, refreshCurrentUser]);

  const signUp = useCallback(
    async (payload: SignUpPayload) => {
      const response = await fetch(`${API_BASE_URL}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          data?.message ??
          "We couldn’t create your account. Please try again or contact support.";
        throw new Error(message);
      }

      applyAuthState(data, payload.rememberMe);
    },
    [applyAuthState]
  );

  const signIn = useCallback(
    async (payload: SignInPayload) => {
      const response = await fetch(`${API_BASE_URL}/auth/signin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json().catch(() => null);

      if (response.status === 402) {
        handleSubscriptionRequired({
          expiresAt: normalizeDate(data?.trialExpiresAt),
          isActive: false,
          subscriptionActive: false,
        });
        const message =
          data?.message ??
          "Your trial has ended. Please activate your subscription to continue.";
        throw new Error(message);
      }

      if (response.status === 401) {
        const message =
          data?.message ?? "Invalid email or password. Please try again.";
        throw new Error(message);
      }

      if (!response.ok) {
        const message =
          data?.message ??
          "Sign in failed. Please check your credentials and try again.";
        throw new Error(message);
      }

      applyAuthState(data, payload.rememberMe);
    },
    [applyAuthState, handleSubscriptionRequired]
  );

  const signOut = useCallback(async () => {
    const { token } = loadToken();

    try {
      if (token) {
        await fetchWithAuth(
          "/auth/signout",
          {
            method: "POST",
          },
          token
        );
      }
    } catch (error) {
      console.warn("Failed to sign out cleanly:", error);
    } finally {
      clearAuthState();
    }
  }, [fetchWithAuth, clearAuthState]);

  const revokeSession = useCallback(
    async (tokenId: string) => {
      if (!tokenId) {
        return;
      }

      try {
        const response = await fetchWithAuth(`/auth/sessions/${tokenId}`, {
          method: "DELETE",
        });

        if (!response.ok) {
          if (response.status === 404) {
            await refreshSessions();
            return;
          }
          if (response.status === 401) {
            return;
          }
          throw new Error("Unable to revoke that session.");
        }

        const data = await response.json().catch(() => null);
        applySessions(data?.sessions);

        if (data?.currentSessionRevoked) {
          clearAuthState();
        }
      } catch (error) {
        console.error("Failed to revoke session:", error);
        throw error;
      }
    },
    [fetchWithAuth, applySessions, clearAuthState, refreshSessions]
  );

  const revokeOtherSessions = useCallback(async () => {
    try {
      const response = await fetchWithAuth("/auth/sessions", {
        method: "DELETE",
      });

      if (!response.ok) {
        if (response.status === 401) {
          return;
        }
        throw new Error("Unable to revoke other sessions.");
      }

      const data = await response.json().catch(() => null);
      applySessions(data?.sessions);
    } catch (error) {
      console.error("Failed to revoke other sessions:", error);
      throw error;
    }
  }, [fetchWithAuth, applySessions]);

  const requestPasswordReset = useCallback(async (email: string) => {
    const normalized = (email || "").trim().toLowerCase();
    if (!normalized) {
      throw new Error("Email is required.");
    }

    const response = await fetch(
      `${API_BASE_URL}/auth/password/reset/request`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalized }),
      }
    );

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const message =
        data?.message ??
        "We couldn’t send that reset code. Please try again in a moment.";
      throw new Error(message);
    }
  }, []);

  const confirmPasswordReset = useCallback(
    async (payload: PasswordResetConfirmPayload) => {
      const response = await fetch(
        `${API_BASE_URL}/auth/password/reset/confirm`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const message =
          data?.message ??
          "We couldn’t verify that code. Please request a new one and try again.";
        throw new Error(message);
      }
    },
    []
  );

  const value = useMemo(
    () => ({
      currentUser,
      loading,
      subscriptionRequired,
      trialInfo,
      signIn,
      signUp,
      signOut,
      clearSubscriptionRequired: () => {
        setSubscriptionRequired(false);
        setTrialInfo(null);
      },
      sessions,
      refreshSessions,
      revokeSession,
      revokeOtherSessions,
      requestPasswordReset,
      confirmPasswordReset,
    }),
    [
      currentUser,
      loading,
      subscriptionRequired,
      trialInfo,
      signIn,
      signUp,
      signOut,
      sessions,
      refreshSessions,
      revokeSession,
      revokeOtherSessions,
      requestPasswordReset,
      confirmPasswordReset,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
